import { useMemo } from 'react';
import { AcademicCapIcon, PlusIcon } from '@heroicons/react/24/outline';
import {
    DataTable,
    type DataTableFilter,
    type DataTableMode,
    type DataTablePagination,
    type DataTableRefs,
    type DataTableSearchConfig,
    type DataTableSort
} from '@odo/shared-ui';
import type {
    OrthodontistListItem,
    OrthodontistsSortBy
} from '../../../services/orthodontistAccounts';
import { buildOrthodontistColumns } from './orthodontistColumns';

const SKELETON_ROW_COUNT = 9;

function SkeletonCell({ width }: { width: string }) {
    return <div className={`h-3.5 rounded bg-gray-200 animate-pulse ${width}`} />;
}

function SkeletonRows() {
    const widths = ['w-44', 'w-28', 'w-8', 'w-8', 'w-20'];
    return (
        <>
            {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
                <tr key={i} className="h-14">
                    {widths.map((width, j) => (
                        <td key={j} className="px-3 first:pl-5 align-middle">
                            <SkeletonCell width={width} />
                        </td>
                    ))}
                    <td className="px-3 last:pr-4 align-middle" />
                </tr>
            ))}
        </>
    );
}

interface EmptyStateProps {
    search: string;
    hasFilters: boolean;
    onClearFilters: () => void;
    onAddOrthodontist: () => void;
}

// Mirrors PracticesTable's EmptyState (search text vs. generic filters message).
function EmptyState({ search, hasFilters, onClearFilters, onAddOrthodontist }: EmptyStateProps) {
    const hasSearch = search.length > 0;
    return (
        <div className="flex flex-col items-center gap-3 text-center">
            <AcademicCapIcon className="h-12 w-12 text-gray-300" strokeWidth={1} />
            <p className="text-sm text-gray-500">
                {hasSearch ? (
                    <>No orthodontists match &ldquo;{search}&rdquo;.</>
                ) : (
                    'No orthodontists match your filters.'
                )}
            </p>
            <div className="flex gap-2.5 mt-1">
                <button
                    type="button"
                    onClick={onClearFilters}
                    className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm"
                >
                    {hasFilters ? 'Clear filters' : 'Clear search'}
                </button>
                <button
                    type="button"
                    onClick={onAddOrthodontist}
                    className="bg-gray-800 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-gray-900 transition-colors"
                >
                    + Add orthodontist
                </button>
            </div>
        </div>
    );
}

interface OrthodontistsTableProps {
    orthodontists: OrthodontistListItem[];
    isLoading: boolean;
    search: string;
    hasFilters: boolean;
    onClearFilters: () => void;
    onOpenOrthodontist: (id: string) => void;
    onAddOrthodontist: () => void;
    sort: DataTableSort<OrthodontistsSortBy>;
    mode: DataTableMode;
    onModeChange: (mode: DataTableMode) => void;
    pagination: DataTablePagination;
    onLoadMore: () => void;
    hasMore: boolean;
    isLoadingMore: boolean;
    searchConfig: DataTableSearchConfig;
    filters: DataTableFilter[];
    refs?: DataTableRefs;
}

export function OrthodontistsTable({
    orthodontists,
    isLoading,
    search,
    hasFilters,
    onClearFilters,
    onOpenOrthodontist,
    onAddOrthodontist,
    sort,
    mode,
    onModeChange,
    pagination,
    onLoadMore,
    hasMore,
    isLoadingMore,
    searchConfig,
    filters,
    refs
}: OrthodontistsTableProps) {
    const columns = useMemo(() => buildOrthodontistColumns(), []);

    return (
        <DataTable
            label="Orthodontists"
            columns={columns}
            rows={orthodontists}
            getRowKey={(orthodontist) => orthodontist.id}
            rowAction={{ onActivate: (orthodontist) => onOpenOrthodontist(orthodontist.id) }}
            rowHeightClassName="h-14"
            sort={sort}
            mode={mode}
            onModeChange={onModeChange}
            pagination={pagination}
            onLoadMore={onLoadMore}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            isLoading={isLoading}
            loadingRows={<SkeletonRows />}
            emptyState={
                <EmptyState
                    search={search}
                    hasFilters={hasFilters}
                    onClearFilters={onClearFilters}
                    onAddOrthodontist={onAddOrthodontist}
                />
            }
            emptyStateCellClassName="px-3 py-16"
            search={searchConfig}
            filters={filters}
            actions={[
                {
                    key: 'add-orthodontist',
                    label: 'Add orthodontist',
                    icon: <PlusIcon className="h-4 w-4" />,
                    variant: 'primary',
                    onClick: onAddOrthodontist
                }
            ]}
            refs={refs}
            className="overflow-hidden px-8 pb-8"
        />
    );
}
